import { useFormContext } from 'react-hook-form';
import { useEffect, useMemo } from 'react';
import {
  CurrencyInputV2,
  DocumentNumberFieldV2,
  FormFieldV2,
  SearchFieldV2 as SearchField
} from '@/components/custom/erp';
import { khachHangSearchColumns, MA_CHUNG_TU } from '@/constants';
import { KhachHang, QuyenChungTu } from '@/types/schemas';
import { usePhieuNhapKhoContext } from '../../context';
import { QUERY_KEYS } from '@/constants/query-keys';
import { Label } from '@/components/ui/label';
import { FormMode } from '@/types/form';

type Transaction = {
  uuid?: string;
  ma_gd?: string;
  ten_gd?: string;
};

interface BasicInfoTabProps {
  formMode: FormMode;
  customer?: KhachHang | null;
  currentTransaction?: Transaction | null;
  soChungTu?: string;
  setCustomer: (customer: KhachHang) => void;
  setQuyenChungTu: (documentBook: QuyenChungTu) => void;
  setSoChungTu: (numberDocument: string) => void;
}

const statusOptions = [
  { value: '0', label: 'Lập chứng từ' },
  { value: '3', label: 'Chờ duyệt' },
  { value: '5', label: 'Nhập kho' },
  { value: '8', label: 'Xuất hoá đơn' }
];

export const BasicInfoTab = ({
  formMode,
  customer,
  currentTransaction,
  soChungTu,
  setCustomer,
  setQuyenChungTu,
  setSoChungTu
}: BasicInfoTabProps) => {
  const isViewMode = formMode === 'view';
  const { watch, setValue } = useFormContext();
  const currentStatus = watch('status');
  const transactionCode = watch('ma_gd');
  const { hasInvoiceParams, transactionOptions } = usePhieuNhapKhoContext();
  const disable = isViewMode || hasInvoiceParams;

  const selectableTransactionOptions = useMemo(() => {
    if (!currentTransaction?.uuid) return transactionOptions;

    const exists = transactionOptions.some(option => String(option.value) === String(currentTransaction.uuid));
    if (exists) return transactionOptions;

    return [
      {
        value: currentTransaction.uuid,
        label: currentTransaction.ten_gd || currentTransaction.ma_gd || currentTransaction.uuid
      },
      ...transactionOptions
    ];
  }, [currentTransaction, transactionOptions]);

  useEffect(() => {
    if (!transactionCode && transactionOptions[0]?.value) {
      setValue('ma_gd', transactionOptions[0].value);
    }
  }, [transactionCode, transactionOptions, setValue]);

  const selectableStatusOptions = statusOptions.filter(option => option.value !== '8' || currentStatus === '8');

  return (
    <div className='space-y-2 p-4'>
      <div className='flex flex-col gap-6 lg:flex-row lg:gap-8'>
        <div className='flex-1 space-y-2'>
          <div className='flex items-center gap-2'>
            <Label className='w-32 min-w-32 text-left'>Giao dịch</Label>
            <div className='w-[260px]'>
              <FormFieldV2 type='select' name='ma_gd' disabled={disable} options={selectableTransactionOptions} />
            </div>
          </div>

          <div className='flex items-center gap-2'>
            <Label className='w-32 min-w-32 text-left'>Mã đối tượng</Label>
            <SearchField<KhachHang>
              type='text'
              searchEndpoint={`/${QUERY_KEYS.KHACH_HANG}`}
              searchColumns={khachHangSearchColumns}
              dialogTitle='Danh mục đối tượng'
              columnDisplay='customer_code'
              displayRelatedField='customer_name'
              value={customer?.customer_code || customer?.customer_number || ''}
              relatedFieldValue={customer?.customer_name || ''}
              onRowSelection={setCustomer}
              disabled={disable}
            />
          </div>

          <div className='flex items-center gap-2'>
            <Label className='w-32 min-w-32 text-left'>Diễn giải</Label>
            <div className='flex-1'>
              <FormFieldV2 type='text' name='dien_giai' disabled={disable} />
            </div>
          </div>
        </div>

        <div className='w-full space-y-3 lg:w-[25rem]'>
          <DocumentNumberFieldV2
            ma_ct={MA_CHUNG_TU.TON_KHO.PHIEU_NHAP_KHO}
            onQuyenChungTuChange={setQuyenChungTu}
            soChungTu={soChungTu || ''}
            onSoChungTuChange={setSoChungTu}
            disabled={disable}
            classNameSearchField='flex-1'
            formMode={formMode}
          />

          <div className='flex items-center gap-2'>
            <Label className='w-32 min-w-32 text-left'>Ngày chứng từ</Label>
            <div className='flex-1'>
              <FormFieldV2 type='date' name='ngay_ct' disabled={disable} />
            </div>
          </div>

          <CurrencyInputV2 formMode={formMode} disabled={disable} />

          <div className='flex items-center gap-2'>
            <Label className='w-32 min-w-32 text-left'>Trạng thái</Label>
            <div className='flex-1'>
              <FormFieldV2 type='select' name='status' disabled={disable} options={selectableStatusOptions} />
            </div>
          </div>

          <div className='flex items-center gap-2'>
            <FormFieldV2
              type='checkbox'
              label='Dữ liệu được nhận'
              name='transfer_yn'
              disabled={disable}
              className='flex items-center'
            />
          </div>
        </div>
      </div>
    </div>
  );
};
